import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../UI/Spinner';

// AuthGuard shows a loading state while AuthContext is initialising or
// after the user clicks logout. Token validation is owned by AuthContext —
// the previous duplicate `validateToken` call here was firing on every
// mount + nav, doubling the `/auth/me` request count.
const AuthGuard = ({ children }) => {
  const { loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = () => setIsTransitioning(true);

    // If we lose the access token while not on the landing page,
    // we're about to be redirected — show the transition state.
    const checkRedirect = () => {
      if (!localStorage.getItem('accessToken') && window.location.pathname !== '/') {
        setIsTransitioning(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    checkRedirect();

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  if (authLoading || isTransitioning) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa] dark:bg-[#171717]">
        <div className="text-center">
          <Spinner size="xl" className="mx-auto mb-4 text-gray-500 dark:text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isTransitioning
              ? t('common.status.redirecting', 'Redirecting...')
              : t('common.status.loading', 'Loading...')}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default AuthGuard;
